import type { CollectedNumber } from "./liveKitTypes";

export interface Base {
	id?: string;
	path?: string;
	createdAt?: Date | string | null;
	updatedAt?: Date | string | null;
}

export const EXTERNAL_SCOPES = [
	"calls:read",
	"calls:write",
	"transcripts:read",
	"callers:read",
	"callers:write",
	"runs:read",
	"company:read",
	"company:write",
	"services:read",
	"services:write",
	"agents:read",
	"agents:write",
	"users:read",
	"users:write",
	"playbooks:read",
	"playbooks:write",
	"runs:execute",
] as const;

export type ExternalScope = (typeof EXTERNAL_SCOPES)[number];

export interface DeveloperKey extends Base {
	companyId: string;
	name: string;
	tokenHash: string;
	scopes: ExternalScope[];
	status: "active" | "revoked";
	createdBy: string;
	revokedAt?: Date | string | null;
	revokedBy?: string | null;
}

export interface User extends Base {
	companyId: string;
	email: string;
	role: "user" | "admin" | "system-admin";
	displayName: string;
	/** Active canonical field for last user login timestamp (standardized on `...At` suffix). */
	lastLoginAt?: Date | string | null;
	/** @deprecated Legacy field superseded by `lastLoginAt`. Kept for backwards compatibility with older Firestore documents and admin views. */
	lastLogin?: Date | string | null;
	phoneNumber?: string;
	language?: "en" | "he";
	internalAuth?: {
		configured?: boolean;
		updatedAt?: Date | string | null;
	};
}

export interface AuthenticatedCompanyUserIdentity {
	companyId: string;
	userId: string;
	role: "user" | "admin" | "system-admin";
	displayName?: string;
}

export interface CompanySipSettings {
	domainSid?: string;
	domainName?: string;
	credentialListSid?: string;
	sipUsernames?: string[];
}

export type CompanyOutboundCallerIdStatus = "verified" | "unverified";

export interface CompanyOutboundCallerId {
	phoneNumber: string;
	status: CompanyOutboundCallerIdStatus;
	twilioOutgoingCallerIdSid?: string | null;
}

export type CompanyCustomerType = "paying" | "trial" | "test";

export interface Company extends Base {
	/** Admin-managed classification, independent of subscription/billing status. */
	customerType?: CompanyCustomerType | null;
	name: string;
	contactName: string;
	contactEmail: string;
	contactPhone: string;
	address?: string;
	/** IANA timezone used for company-level reporting and unattributed activity. */
	timeZone?: string;
	/** Undefined uses the 120-day default; null means call text and GCP audio do not expire. */
	retentionDays?: number | null;
	onboarding?: EntityOnboardingMetadata;
	features?: {
		callerMemoryUserUi?: boolean;
		callerMemoryAgentInjection?: boolean;
		outboundPlaybookCalls?: boolean;
		elevenLabsProvider?: boolean;
		apiAccess?: boolean;
		piiRemoval?: boolean;
		deleteAudioFromTwilio?: boolean;
		deletePiiJsonFromGcp?: boolean;
	};
	whatsappConnection?: {
		status: "processing" | "connected" | "failed";
		credentialMode?: "owned" | "borrowed";
		sourceProject?: string;
		expiresAt?: string;
		active?: boolean;
		responsesEnabled?: boolean;
		responseLeaseExpiresAt?: string;
	};
	sipSettings?: CompanySipSettings;
	outboundCallerIds?: CompanyOutboundCallerId[];
}

export type AgentStatus = "Active" | "Paused" | "Draft";

export type InboundAnsweringPolicy =
	| "AI_FIRST"
	| "AI_ONLY"
	| "HUMAN_FIRST"
	| "HUMAN_ONLY";

export type WeeklyScheduleDay =
	| "sunday"
	| "monday"
	| "tuesday"
	| "wednesday"
	| "thursday"
	| "friday"
	| "saturday";

export interface WeeklyScheduleWindow {
	days: WeeklyScheduleDay[];
	/** Local wall-clock time in HH:mm format. */
	startTime: string;
	/** Local wall-clock time in HH:mm format. 24:00 is allowed as an end boundary. */
	endTime: string;
}

export type TransferDestinationAvailability =
	| { mode: "always" }
	| { mode: "custom"; windows: WeeklyScheduleWindow[] };

export interface TransferToHumanDestination {
	id: string;
	type: "pstn" | "sip";
	address: string;
	/** Lower numbers are attempted first. */
	priority: number;
	availability: TransferDestinationAvailability;
}

export type DeploymentStatus =
	| "creating_pr"
	| "pr_opened"
	| "pr_validation_started"
	| "pr_merged"
	| "merge_success"
	| "deploy_started"
	| "started"
	| "deploy_success"
	| "deploy_error"
	| "merge_error"
	| "error";

export interface AgentPolicySection {
	id: string;
	title: string;
	content: string;
}

export interface AgentMessageTemplate {
	id: string;
	name: string;
	text: string;
	description: string;
}

export interface AgentTypicalFlow {
	id: string;
	name: string;
	description: string;
	example: string;
}

export type AgentProvider = "livekit" | "elevenlabs";

/** Confirmed call routing, retained while the latest attempt changes status. */
export interface AgentDeploymentRouting {
	provider: AgentProvider;
	elevenlabs?: {
		agentId: string;
		sipUri: string;
		branchId?: string;
		phoneNumberId?: string;
		workspaceId?: string;
		credentialVersion?: string;
	};
}

export interface AgentDeployment {
	id: string;
	createdAt?: number;
	status?: DeploymentStatus;
	updatedAt?: number;
	serviceId?: string | null;
	serviceUpdatedAt?: number | null;
	version?: number;
	prUrl?: string;
	actionUrl?: string;
	artifactId?: string;
	artifactUrl?: string;
	action?: string;
	error?: string;
	rollbackToVersion?: number;
	provider?: AgentProvider;
	routing?: AgentDeploymentRouting;
	agentcRevision?: string;
	sourceCommitSha?: string;
	mergeSha?: string;
	elevenlabs?: {
		agentId: string;
		branchId: string;
		versionId: string;
		workspaceId: string;
	};
}

export interface AgentContentFields {
	greeting?: string;
	goal?: string;
	referenceMaterials?: { name: string; size: string }[];
	gender?: string;
	tone?: string;
	voice?: string;
	realtime?: {
		provider?: string;
		model?: string;
		effort?: RealtimeEffort | null;
	};
	gptLive?: {
		enabled?: boolean;
		voice?: string;
		reasoningModel?: string;
	};
	defaultLanguage?: string;
	timeZone?: string;
	dos?: string;
	donts?: string;
	policySections?: AgentPolicySection[];
	messagesSenderName?: string;
	messages?: AgentMessageTemplate[];
	typicalFlows?: AgentTypicalFlow[];
	dtmfCollectionTools?: {
		askForPhoneNumber?: boolean;
		askForLicenseNumber?: boolean;
		askForIdNumber?: boolean;
	};
}

export type OnboardingMode = "self_service" | "assisted";

export type OnboardingStatus =
	| "provisioning_started"
	| "enrichment_queued"
	| "enrichment_running"
	| "review_required"
	| "demo_ready"
	| "failed";

export interface OnboardingCustomerDetails {
	name?: string;
	email?: string;
	mobilePhone?: string;
	businessPhone?: string;
	website?: string;
	description?: string;
	notes?: string;
}

export interface EntityOnboardingMetadata {
	onboardingId: string;
	mode: OnboardingMode;
	status: OnboardingStatus;
	createdBy: string;
	customer?: OnboardingCustomerDetails;
}

export type RealtimeEffort = "minimal" | "low" | "medium" | "high" | "xhigh";

export interface AgentOperationFields {
	/** Reply to incoming WhatsApp texts. Missing values are disabled. */
	whatsappRespondToMessages?: boolean;
	hasUncommittedChanges?: boolean;
	stagingDeployment?: AgentDeployment;
	liveDeployment?: AgentDeployment;
	liveDeploymentVersions?: number[];
	lastCall?: string | null;
}

export interface AgentStats {
	todayDateKey?: string;
	callsToday?: number;
	minutesToday?: number;
	totalCalls?: number;
	totalMinutes?: number;
	goalsToday?: number;
	totalGoals?: number;
	blockedCalls?: number;
	testCalls?: number;
}

export interface AgentReportingFields {
	stats?: AgentStats;
}

export type AgentTemplateOverrideBackend =
	| "livekit"
	| "livekit_plugin"
	| "_shared_livekit"
	| "agent_generator";

export interface AgentTemplateOverride extends Base {
	templateId: string;
	templatePath: string;
	backend: AgentTemplateOverrideBackend;
	content: string;
	enabled: boolean;
	updatedBy?: string;
	checksum?: string;
	notes?: string;
}

export interface AgentcTemplateListItem {
	templateId: string;
	templatePath: string;
	backend: AgentTemplateOverrideBackend;
	sourcePath: string;
	size?: number;
	sha: string;
}

export interface ListAgentcTemplatesResponse {
	templates: AgentcTemplateListItem[];
	cached: boolean;
	fetchedAt: string;
	expiresAt: string;
}

export interface GetAgentcTemplateContentRequest {
	templatePath: string;
}

export interface GetAgentcTemplateContentResponse {
	template: AgentcTemplateListItem;
	content: string;
	cached: boolean;
	fetchedAt: string;
	expiresAt: string;
}

export interface ElevenLabsAgentConfig {
	/** KMS ciphertext; only backend provisioning may write provider configuration. */
	encryptedSipCredentials?: string;
	agentId: string;
	sipUris: { test: string; live: string };
	workspaceId?: string;
	credentialVersion?: string;
	branchIds?: { test: string; live: string };
	phoneNumberIds?: { test: string; live: string };
}

export interface Agent
	extends Base,
		AgentContentFields,
		AgentOperationFields,
		AgentReportingFields {
	name: string;
	companyId: string;
	/** Missing provider retains legacy LiveKit routing. */
	provider?: "livekit" | "elevenlabs";
	/** Editable source choice for the next Testing deployment; never a routing switch. */
	draftProvider?: AgentProvider;
	/** Remember voice choices independently when changing draft provider. */
	providerVoiceSettings?: Partial<
		Record<AgentProvider, { voice?: string; language?: string }>
	>;
	elevenlabs?: ElevenLabsAgentConfig;
	status: AgentStatus;
	inboundAnsweringPolicy?: InboundAnsweringPolicy;
	/** Reply to incoming WhatsApp texts. Missing values are disabled. */
	whatsappRespondToMessages?: boolean;
	phoneNumbers: string[];
	blacklistedCallerNumbers?: string[];
	testerCallerNumbers?: string[];
	transferToHumanPhoneNumber?: string | null;
	transferToHumanPhoneNumbers?: string[];
	transferToHumanDestinations?: TransferToHumanDestination[];
	phoneNumberDetails?: {
		/** @deprecated Testing numbers are shared platform resources. */
		testing?: PhoneNumberAssignment[];
		live: PhoneNumberAssignment[];
	};
	version: number;
	serviceId?: string | null;
	service?: Service | null;
	onboarding?: EntityOnboardingMetadata;
}

export interface ServiceResource extends Base {
	name: string;
	link: string;
	type: "pdf" | "doc" | "url" | "txt";
}

export type ServiceTagColor =
	| "slate"
	| "gray"
	| "blue"
	| "cyan"
	| "teal"
	| "green"
	| "yellow"
	| "amber"
	| "orange"
	| "red"
	| "pink"
	| "purple"
	| "indigo";

export interface ServiceTag extends Base {
	serviceId: string;
	label: string;
	description: string;
	color: ServiceTagColor;
	deletedAt?: Date | string | null;
}

export interface ServiceNotificationSettings {
	enabled: boolean;
	emails: string[];
	fromName: string;
	subject: string;
	body: string;
}

export interface Service extends Base {
	name: string;
	companyId: string;
	description: string;
	website?: string;
	serviceBrief?: string;
	contactDetails?: string;
	faq?: string;
	notifications?: ServiceNotificationSettings;
	knowledgeBase?: string;
	additionalResources: ServiceResource[];
	isActive?: boolean;
	tags?: ServiceTag[];
	onboarding?: EntityOnboardingMetadata;
}

export interface PhoneNumberAssignment {
	number: string;
	status: "active" | "disabled";
	channel: "live" | "test" | "unassigned";
	agentId?: string | null;
	companyId?: string | null;
}

export type WhatsappMetaEntity = "danj-consulting" | "provoize";
export type WhatsappTemplateName = "request_acknowledgement" | "hello_world";

export interface WhatsappSender extends Base {
	metaPhoneNumberId: string;
	businessPhoneNumber: string;
	wabaId?: string | null;
	companyId: string;
	agentId?: string | null;
	metaEntity: WhatsappMetaEntity;
	status: "active" | "disabled";
}

export interface PhoneNumberAgentSync {
	status: "pending" | "completed" | "error" | "skipped";
	syncedAt?: Date | string | null;
	error?: string | null;
	affectedCompanyIds?: string[];
	affectedAgentCount?: number;
}

export interface PhoneNumber extends Base {
	number: string;
	provider: string;
	status: "active" | "disabled";
	channel: "live" | "test" | "unassigned";
	agentId?: string | null;
	companyId?: string | null;
	lastCallAt?: Date | null;
	monthlyCostInCents: number; // in cents
	agentSync?: PhoneNumberAgentSync;
}

export const PHONE_NUMBER_FIELDS = [
	"id",
	"path",
	"number",
	"provider",
	"status",
	"channel",
	"agentId",
	"companyId",
	"lastCallAt",
	"createdAt",
	"updatedAt",
	"monthlyCostInCents",
	"agentSync",
] as const satisfies readonly (keyof PhoneNumber)[];

export const PHONE_NUMBER_AGENT_SYNC_TRIGGER_FIELDS = [
	"number",
	"status",
	"channel",
	"agentId",
	"companyId",
] as const satisfies readonly (keyof PhoneNumber)[];

export const CALL_STATUS = {
	PROCESSING: "processing",
	COMPLETED: "completed",
	IN_PROGRESS: "in-progress",
	FAILED: "failed",
	ABANDONED: "abandoned",
	ONGOING: "ongoing",
} as const;

export const CALL_STATUSES = Object.values(CALL_STATUS);

export type CallStatus = (typeof CALL_STATUSES)[number];

export interface TranscriptionWarning {
	code: "single-transcript-warning" | "transcript-unavailable";
	failedRecognizerIds: string[];
	successfulRecognizerIds: string[];
}

export type PiiProcessingStepStatus =
	| "disabled"
	| "not_required"
	| "pending"
	| "completed"
	| "failed";

export type PiiRedactionAttemptStatus =
	| "running"
	| "failed"
	| "needs_recovery"
	| "completed";

export type PiiRedactionArtifactStatus =
	| "pending"
	| "running"
	| "delete_requested"
	| "completed"
	| "failed"
	| "not_required"
	| "retained_by_policy";

export interface PiiRedactionArtifactState {
	status: PiiRedactionArtifactStatus;
	startedAt?: Date | string | null;
	completedAt?: Date | string | null;
	errorCode?: string | null;
	errorMessage?: string | null;
	inputUri?: string | null;
	inputGeneration?: string | null;
	outputUri?: string | null;
	outputGeneration?: string | null;
	metadata?: Record<string, string | number | boolean | null>;
}

export interface PiiRedactionAttempt {
	id: string;
	workflowVersion: number;
	companyId: string;
	callSid: string;
	trigger: "automatic" | "manual";
	status: PiiRedactionAttemptStatus;
	artifacts: Record<string, PiiRedactionArtifactState>;
	flags: {
		piiRemoval: boolean;
		deletePiiJsonFromGcp: boolean;
		deleteAudioFromTwilio: boolean;
	};
	createdAt?: Date | string | null;
	updatedAt?: Date | string | null;
	completedAt?: Date | string | null;
	failureStep?: string | null;
	failureReason?: string | null;
	humanReview?: PiiHumanReview | null;
}

export type PiiTextAlignmentMethod = "canonical" | "contextual" | "llm";

export interface PiiTextAlignmentSummary {
	status: "completed" | "requires_human_review";
	methodsUsed: PiiTextAlignmentMethod[];
	finalMethod?: PiiTextAlignmentMethod | null;
	finalConfidence?: number | null;
	transcriptCount: number;
	expectedOccurrences: number;
	resolvedOccurrences: number;
	matchedOccurrences: number;
	notPresentOccurrences: number;
	requiresHumanReview: boolean;
}

export interface PiiHumanReview {
	status: "processing" | "approved" | "failed" | "rejected";
	decision: "accept_available_matches" | "request_manual_resolution";
	/** Attempt reviewed; an older rejection must not describe a later retry. */
	attemptId?: string;
	reviewedBy: string;
	reason: string;
	reviewedAt?: Date | string | null;
	completedAt?: Date | string | null;
	failureReason?: string | null;
	originalAlignment: PiiTextAlignmentSummary;
}

export interface PiiProcessingState {
	correctionOpen?: boolean;
	inProgress?: boolean;
	detection: PiiProcessingStepStatus;
	text: PiiProcessingStepStatus;
	audio: PiiProcessingStepStatus;
	transcriptArtifacts: PiiProcessingStepStatus;
	entitiesCount: number;
	failureReason?: string | null;
	attemptId?: string | null;
	workflowVersion?: number;
	alignment?: PiiTextAlignmentSummary | null;
	requiresHumanReview?: boolean;
	humanReview?: PiiHumanReview | null;
}

export function isCallStatus(value: unknown): value is CallStatus {
	return (
		typeof value === "string" &&
		(CALL_STATUSES as readonly string[]).includes(value)
	);
}

//
//
//
export interface Call extends Base {
	companyId: string;
	serviceId: string;
	agentId: string;
	phoneNumberId: string;
	caseId?: number;
	isTestCall?: boolean;
	isBlocked?: boolean;
	// createdAt, endsAt are handled by Base or separate fields if needed, but the commented out code had strings.
	// Using Base for dates is better if standardized.
	// However, keeping consistent with what was there:
	// createdAt: string;
	endedAt?: string;
	status: CallStatus;
	interactionStatus?: "new" | "read" | "unread" | "archived";
	cost?: number; // in cents
	callerName?: string;
	/** Active field for caller phone number in E.164 format. */
	callerPhoneNumber?: string;
	callerEmail?: string;
	summary?: string;
	gcsUri?: string;
	/** @deprecated Raw transcript string. Use `formattedTranscript` for formatted/structured transcripts. */
	transcript?: string;
	/** Active field for formatted/structured call transcript. */
	formattedTranscript?: string;
	transcriptionWarning?: TranscriptionWarning | null;
	/** Company-user feedback about the accuracy of the generated transcript. */
	transcriptFeedback?: {
		rating: "positive" | "negative";
		comment?: string | null;
		submittedBy: string;
		submittedAt: Date | string;
	} | null;
	/** Latest user correction. The generated transcript remains in `formattedTranscript`. */
	transcriptEdit?: {
		text: string;
		hash: string;
		revisionId: string;
		editedBy: string;
		editedAt: Date | string;
	} | null;
	collectedNumbers?: CollectedNumber[];
	agentName?: string;
	/** Active field for agent assigned phone number. */
	agentPhoneNumber?: string;
	/** @deprecated Unformatted duration. Use `durationSeconds` for numeric duration in seconds. */
	duration?: number;
	/** Active field for call duration in seconds. */
	durationSeconds?: number;
	agentTimeZone?: string;
	/** Company timezone captured when an agent-less call first contributes to daily statistics. */
	companyStatsTimeZone?: string;
	title?: string;
	notes?: string;
	/** Active array of associated ServiceTag IDs. */
	tagIds?: string[];
	/** @deprecated Legacy tags array. Use `tagIds` for tag references. */
	tags?: string[];
	evaluationScore?: number;
	evaluationRubric?: "inbound_receptionist" | "outbound_playbook" | null;
	evaluationStatus?: "pending" | "completed" | "error";
	isProblematic?: boolean;
	evaluatedAt?: Date | string | null;
	evaluationUsage?: {
		inputTokens?: number;
		outputTokens?: number;
		totalTokens?: number;
	} | null;
	evaluationCost?: number | null;
	source?: "outbound_playbook_task" | string | null;
	outboundPlaybookRunId?: string | null;
	outboundTaskId?: string | null;
	outboundTaskAttemptId?: string | null;
	outboundPlaybookId?: string | null;
	outboundPlaybookVersion?: number | null;
	destinationNumber?: string | null;
	operatorPhoneNumber?: string | null;
	operatorName?: string | null;
	callType?: "ai" | "human" | null;
	direction?: "inbound" | "outbound" | null;
	isOutbound?: boolean;
	/** @deprecated Raw Twilio 'from' parameter. Use `callerPhoneNumber`. */
	from?: string;
	/** @deprecated Raw Twilio 'to' parameter. Use `agentPhoneNumber` or `inboundNumber`. */
	to?: string;
	isPiiRedacted?: boolean;
	piiRedactedAt?: Date | string | null;
	piiProcessing?: PiiProcessingState | null;
	isTwilioRecordingPurged?: boolean;
	twilioRecordingPurgedAt?: Date | string | null;
	isPiiSidecarPurged?: boolean;
	piiSidecarPurgedAt?: Date | string | null;
	piiSidecarPurgeOutcome?: "deleted" | "already_absent" | null;
}
export interface ProcessRawCallPayload {
	callSid: string;
	transcripts: {
		recognizerId: string;
		transcriptText?: string;
		structuredItems?: { role: string; text: string }[];
	}[];
}

export interface ProcessRawCallResponse {
	formattedTranscript: string;
}

// From tcdrs.ts
export interface TranscriptSegment {
	role: string;
	text: string;
}

export interface TranscriptEntry {
	recognizerId: string;
	transcriptText: string;
	structuredSegments?: TranscriptSegment[];
}

export interface RecordingManifest {
	gcsUri?: string;
	transcripts?: TranscriptEntry[];
	downloadUrl?: string;
}

export type TCDR = Record<string, unknown> & {
	sid: string;
	accountSid?: string;
	answeredBy?: string;
	apiVersion?: string;
	callerName?: string;
	dateCreated?: string;
	dateUpdated?: string;
	direction?: string;
	duration?: string;
	endTime?: string;
	forwardedFrom?: string;
	from?: string;
	fromFormatted?: string;
	groupSid?: string;
	parentCallSid?: string;
	phoneNumberSid?: string;
	price?: string;
	priceUnit?: string;
	queueTime?: string;
	startTime?: string;
	status?: string;
	subresourceUris?: Record<string, unknown>;
	to?: string;
	toFormatted?: string;
	transcript?: string;
	transcripts?: TranscriptEntry[];
	trunkSid?: string;
	uri?: string;
	callPath?: string;
};

export interface TcdrRecord {
	id: string; // `${callSid}.${index}`
	raw: Record<string, unknown>;
	manifestPath?: string;
	callPath?: string;
}

export interface CallPathInfo {
	callPath: string;
	phoneNumber: string;
	callSid: string;
}

export interface InviteUserToCompanyRequest {
	companyId: string;
	email: string;
	displayName: string;
	phoneNumber?: string;
	role: "user" | "admin";
}

export interface InviteUserToCompanyResponse {
	success: boolean;
	status: "invited" | "added" | "already-member";
	userId: string;
}

export type ActionItemStatus = "open" | "in-progress" | "closed";

export interface ActionItem extends Base {
	callSid: string;
	createdAt: Date | null;
	status: ActionItemStatus;
	/** Set when the item closes; cleared if it is reopened. */
	closedAt?: Date | null;
	/** Firestore TTL is null while the item is open or in progress. */
	expireAt?: Date | null;
	/** requestedByAgent is retained for historical records; new items are manual. */
	type: "requestedByAgent" | "manual";
	title?: string;
	action: FollowUpActionDetails | BookingActionDetails;
	notes?: string;
	tags?: string[];
}

export interface FollowUpActionDetails {
	type: "followUp";
	customerName: string;
	phoneNumber: string;
	email?: string;
	urgency: "low" | "urgent" | "normal";
	details: string;
	agent: string;
}

export interface BookingActionDetails {
	type: "booking";
	customerName: string;
	phoneNumber: string;
	bookingDate: string;
	duration: number;
	accommodationType: string;
}

export interface RecentInteraction {
	callId: string;
	callCreatedAt: Date;
	topic: string;
	outcome: string;
	summary: string;
}

export interface CallerMemory {
	language: string; // BCP-47
	summary: string;
	facts: string[];
	preferences: string[];
	recentInteractions: RecentInteraction[];
	version: number;
	updatedAt: Date;
	lastProcessedCallId: string;
}

export type CallerInstructionMode = "one_time" | "persistent" | "valid_until";

export type CallerInstructionStatus =
	| "pending"
	| "delivered"
	| "acknowledged"
	| "completed"
	| "canceled"
	| "expired";

export type CallerInstructionDeliveryOutcome =
	| "delivered"
	| "acknowledged"
	| "completed"
	| "incomplete"
	| "failed"
	| "not_applicable";

export interface CallerInstruction extends Base {
	companyId: string;
	callerId: string;
	instruction: string;
	mode: CallerInstructionMode;
	status: CallerInstructionStatus;
	active: boolean;
	priority: number;
	validTill?: Date | null;
	createdBy: { userId: string; displayName?: string };
	updatedBy?: { userId: string; displayName?: string } | null;
	canceledAt?: Date | null;
	canceledBy?: { userId: string; displayName?: string } | null;
	cancelReason?: string | null;
	deliveredAt?: Date | null;
	deliveredCallId?: string | null;
	deliveredByAgentId?: string | null;
	acknowledgedAt?: Date | null;
	acknowledgedCallId?: string | null;
	completedAt?: Date | null;
	completedCallId?: string | null;
	lastDeliveryAttemptAt?: Date | null;
	lastDeliveryOutcome?: CallerInstructionDeliveryOutcome | null;
	lastDeliveryNote?: string | null;
	deliveryAttemptCount: number;
}

export type MemoryUpdateStatus =
	| "pending"
	| "processing"
	| "completed"
	| "skipped"
	| "failed";

export type MemorySkipReason =
	| "no_caller_id"
	| "invalid_caller_id"
	| "no_caller_speech"
	| "too_short"
	| "no_relevant_information";

export interface Caller extends Base {
	companyId: string;
	phoneNumber: string;
	name: string;
	email: string | null;
	lastCallAt: Date | null;
	notes: string | null;
	tags: string[];

	preferredLanguage?: string; // BCP-47
	preferredLanguageSource?: "explicit" | "detected";
	preferredLanguageUpdatedAt?: Date | null;

	firstCallAt: Date | null;
	callCount: number;

	memoryGeneration: number;
	memoryClearedAt?: Date | null;
	memoryClearedBy?: string;

	memory?: CallerMemory;
	latestMemoryUpdateId?: string;
	latestMemoryUpdateStatus?: MemoryUpdateStatus;
	latestMemoryUpdateAt?: Date | null;
	latestMemoryUpdateCallId?: string;
	latestMemoryUpdateErrorCode?: string;
}

export interface CallerMemoryDelta {
	hasChanges: boolean;
	skipReason?: MemorySkipReason;
	language: string; // BCP-47
	facts: string[];
	preferences: string[];
	interaction?: {
		topic: string;
		outcome: string;
		summary: string;
	};
}

export interface ProposedMemoryChanges {
	addedFacts: string[];
	removedFacts: string[];
	addedPreferences: string[];
	removedPreferences: string[];
	summaryChanged: boolean;
	previousMemoryLanguage?: string;
	resultingMemoryLanguage: string;
	translatedMemory: boolean;
	explanation?: string;
}

export interface ModelUsageAudit {
	provider: "vertex-ai";
	model: string;
	prompt: string;
	usage: {
		inputTokens: number;
		outputTokens: number;
		thoughtsTokens?: number;
		billableOutputTokens?: number;
		totalTokens: number;
	};
	pricing: {
		currency: "USD";
		inputUsdPerMillionTokens: number;
		outputUsdPerMillionTokens: number;
		source: string;
		asOf: string;
	};
	estimatedCostUsd: {
		input: number;
		output: number;
		total: number;
	};
}

export interface BaseCallerMemoryUpdate extends Base {
	status: MemoryUpdateStatus;
	attempts: number;
	processingStartedAt?: Date | null;
	processingStage?: "extraction" | "consolidation";
	completedAt?: Date | null;
	extractionAttempts?: number;

	memoryGeneration: number;
	delta?: CallerMemoryDelta;

	basedOnVersion?: number;
	resultingVersion?: number;
	proposedChanges?: ProposedMemoryChanges;
	resultingMemory?: CallerMemory;
	modelUsage?: {
		extraction?: ModelUsageAudit;
		consolidation?: ModelUsageAudit;
	};
	modelInput?: {
		extraction?: {
			content: string;
			contentType: "transcript_context" | "transcript" | "summary";
			serviceTagsContext?: string;
		};
		consolidation?: {
			existingMemory: CallerMemory | null;
			memoryDelta: CallerMemoryDelta;
			targetLanguage: string;
		};
	};

	skipReason?: MemorySkipReason;
	errorCode?: string;
	errorMessage?: string;

	// Human authoring fields (may be present on some system/backfill actions optionally, but required for user)
	submittedText?: string;
	category?: "fact" | "preference";
	reference?: string;
	effectiveAt?: Date | null;
	informationExpiresAt?: Date | null;
	internalNote?: string;
	sourceLanguage?: string; // BCP-47

	// TTL field
	expireAt?: Date | null;
}

export interface CallCallerMemoryUpdate extends BaseCallerMemoryUpdate {
	source: "call" | "backfill";
	callId: string;
	callCreatedAt: Date;
	backfillRunId?: string;
}

export interface UserCallerMemoryUpdate extends BaseCallerMemoryUpdate {
	source: "user";
	createdBy: {
		userId: string;
		displayName?: string;
	};
}

export interface SystemCallerMemoryUpdate extends BaseCallerMemoryUpdate {
	source: "system";
}

export type CallerMemoryUpdate =
	| CallCallerMemoryUpdate
	| UserCallerMemoryUpdate
	| SystemCallerMemoryUpdate;

export interface SystemSettings extends Base {
	retentionAudit?: {
		scheduledEnabled?: boolean;
		/** Whole hours between successful scheduled batches; defaults to 24. */
		intervalHours?: number;
	};
	webAppBaseUrl?: string;
	callerMemory?: {
		userUiEnabled?: boolean;
		agentInjectionEnabled?: boolean;
		disabledForAll?: boolean;
	};
	elevenLabsProvider?: {
		/** Off by default; companies may explicitly override it. */
		defaultEnabled?: boolean;
	};
	outboundPlaybookCalls?: {
		defaultEnabled?: boolean;
		disabledForAll?: boolean;
		dispatcherEnabled?: boolean;
		dispatcherCallsPerRun?: number;
	};
	piiRemoval?: {
		defaultEnabled?: boolean;
		disabledForAll?: boolean;
	};
	deleteAudioFromTwilio?: {
		defaultEnabled?: boolean;
		disabledForAll?: boolean;
	};
	deletePiiJsonFromGcp?: {
		defaultEnabled?: boolean;
		disabledForAll?: boolean;
	};
}

export type AuditLogType =
	| "user_login"
	| "user_logout"
	| "agent_created"
	| "agent_updated"
	| "agent_deleted"
	| "agent_deployed"
	| "agent_paused"
	| "agent_activated"
	| "service_created"
	| "service_updated"
	| "service_deleted"
	| "company_updated"
	| "phone_number_assigned"
	| "phone_number_unassigned"
	| "phone_number_created"
	| "phone_number_updated"
	| "phone_number_deleted"
	| "user_created"
	| "user_updated"
	| "user_deleted"
	| "company_user_internal_auth_phrase_updated"
	| "subscription_created"
	| "subscription_updated"
	| "subscription_canceled"
	| "subscription_renewal_completed"
	| "billing_record_created"
	| "billing_record_updated"
	| "billing_record_deleted"
	| "caller_memory_updated"
	| "caller_memory_cleared"
	| "call_pii_sidecar_viewed"
	| "call_deleted"
	| "action_item_created"
	| "action_item_updated"
	| "action_item_deleted"
	| "caller_deleted"
	| "internal_interaction_updated"
	| "caller_instruction_created"
	| "caller_instruction_updated"
	| "caller_instruction_canceled"
	| "developer_key_created"
	| "developer_key_revoked"
	| "external_outbound_execution_policy_updated"
	| "external_api_invocation"
	| "caller_instruction_delivery_reported";

export interface AuditLog extends Base {
	timestamp: Date | string;
	userId: string;
	type: AuditLogType;
	phoneNumber?: string;
	companyId?: string;
	serviceId?: string;
	agentId?: string;
	data?: Record<string, unknown>;
}

export interface AdminAction extends Base {
	companyId: string;
	subscriptionId: string;
	type: "cancellation_requested" | "registration_review" | string;
	title: string;
	status: "open" | "done";
	dueDate?: Date | string | null;
	completedAt?: Date | string | null;
	completedBy?: string | null;
	registrationRequestId?: string | null;
	email?: string | null;
	name?: string | null;
	companyWebsite?: string | null;
	callId?: string | null;
	caseId?: number | null;
	processingFailure?: {
		taskId: string;
		phoneNumber: string;
		errorMessage: string;
		failedAt: Date | string | null;
	} | null;
	feedbackRating?: "positive" | "negative" | null;
	feedbackComment?: string | null;
	feedbackSubmittedBy?: string | null;
	feedbackSubmittedAt?: Date | string | null;
	apiFeedback?: {
		category: "bug" | "feature_request" | "documentation" | "other";
		relatedTool?: string | null;
		relatedRequestId?: string | null;
		clientId?: string | null;
		credentialId: string;
		reportedApiVersion?: string | null;
		serverApiVersion?: string | null;
		implementationStatus?: "received" | "in_progress" | "fixed" | null;
	} | null;
}

export interface RegistrationRequest extends Base {
	email: string;
	name: string;
	companyWebsite: string;
	googleUid?: string | null;
	providerId?: string | null;
	status: "pending" | "approved" | "rejected";
	reviewedAt?: Date | string | null;
	reviewedBy?: string | null;
}

// ---------------------------------------------------------------------------
// Daily Call Statistics
// ---------------------------------------------------------------------------

/** Reserved stats bucket for published calls that are not associated with an agent. */
export const COMPANY_DAILY_STATS_BUCKET_ID = "_company";

/** Backend-owned audit findings. Resolution is manual in phase one. */
export interface RetentionAuditFinding {
	id?: string;
	rule:
		| "document_overdue"
		| "expiry_mismatch"
		| "gcs_overdue"
		| "twilio_cleanup_missed"
		| "processing_stalled"
		| "unclassified_path"
		| "parent_missing"
		| "retention_policy_violation";
	companyId: string | null;
	callSid: string | null;
	resourcePath: string;
	status: "open" | "resolved";
	summary: string;
	evidence: Record<string, string | number | boolean | null>;
	expectedDeleteAt: Date | null;
	firstSeenAt: Date;
	lastSeenAt: Date;
	occurrences: number;
	resolutionNote: string | null;
	resolvedAt: Date | null;
	resolvedBy: string | null;
}
